// Herkomst- en marketingattributie voor bestellingen (MOW-ATTRIBUTION-OKT26)
//
// Registreert via welk kanaal een bezoeker binnenkomt: Meta, Google Ads, Google organisch,
// Nieuwsbrief (Klaviyo), social media, een verwijzende website of direct. Bij het afrekenen
// wordt deze herkomst meegestuurd en bij de bestelling bewaard, zodat in het beheerpaneel
// (/admin/bestellingen) te zien is welke kanalen bestellingen opleveren.
//
// Privacy: alleen first-party opslag in de browser (localStorage, geen cookie, geen IP). Het
// bijhouden gebeurt PAS na analytics-toestemming (components/AttributionTracker.tsx) en stopt
// zodra die wordt ingetrokken. Klik-ID's (fbclid/gclid/ttclid) worden alleen bewaard bij
// marketing-toestemming; zonder toestemming wordt er niets opgeslagen en blijft de herkomst van
// de bestelling leeg ("onbekend"), niet "direct". Dit bestand heeft geen imports, zodat de server
// (kassa) en Node-tests het rechtstreeks kunnen laden.

export type AttributionChannel =
  | "meta_ads"
  | "google_ads"
  | "google_organic"
  | "organic_search"
  | "social_organic"
  | "tiktok_ads"
  | "email"
  | "referral"
  | "direct";

export const ATTRIBUTION_CHANNELS: AttributionChannel[] = [
  "meta_ads",
  "google_ads",
  "google_organic",
  "organic_search",
  "social_organic",
  "tiktok_ads",
  "email",
  "referral",
  "direct",
];

export interface OrderAttribution {
  channel: AttributionChannel;
  channelLabel: string;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  utmTerm?: string | null;
  clickId?: string | null;
  clickIdType?: "fbclid" | "gclid" | "ttclid" | null;
  referrer?: string | null;
  referrerHost?: string | null;
  landingPage?: string | null;
  device?: "mobile" | "desktop" | "tablet";
  firstTouchAt?: string;
  lastTouchAt?: string;
}

const STORAGE_KEY = "mow_attribution";
const REFERRER_KEY = "mow_attr_ref";
const ATTRIBUTION_WINDOW_DAYS = 30;

// Verwijzers die geen herkomst zijn: de betaalprovider waar de klant van terugkomt, en de inlogdienst.
const NEGEER_VERWIJZERS = ["mollie.com", "paypal.com", "klarna.com", "accounts.google.com", "firebaseapp.com"];

// Parameters die nooit in de bewaarde landingspagina mogen komen (klik-ID's en de bestelcode/token).
const GEWEERDE_PARAMS = ["fbclid", "gclid", "ttclid", "order", "t", "token"];

/** Bepaalt het apparaattype op basis van user-agent. */
export function detectDevice(ua: string = ""): "mobile" | "desktop" | "tablet" {
  if (/ipad|tablet/i.test(ua)) return "tablet";
  if (/mobi|android|iphone|ipod/i.test(ua)) return "mobile";
  return "desktop";
}

/** Haalt de hostnaam uit een URL zonder protocol en www. */
export function extractHost(urlStr?: string | null): string | null {
  if (!urlStr) return null;
  try {
    const parsed = new URL(urlStr);
    return parsed.hostname.replace(/^www\./, "").toLowerCase();
  } catch {
    return null;
  }
}

/** True voor een verwijzer die nooit als herkomst telt (betaalprovider, inlogdienst). */
export function isNegeerVerwijzer(host?: string | null): boolean {
  if (!host) return false;
  const h = host.toLowerCase();
  return NEGEER_VERWIJZERS.some((d) => h === d || h.endsWith("." + d));
}

// Een bronnaam als los woord: "ig" in "digital" telt niet, "facebook_ads" wel.
function woord(tekst: string, ...woorden: string[]): boolean {
  return new RegExp(`(^|[^a-z0-9])(${woorden.join("|")})($|[^a-z0-9])`).test(tekst);
}

/** Classificeert de verkeersbron naar een duidelijk kanaal en label. */
export function classifyChannel(params: {
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  fbclid?: string | null;
  gclid?: string | null;
  ttclid?: string | null;
  referrerHost?: string | null;
}): { channel: AttributionChannel; channelLabel: string } {
  const src = (params.utmSource || "").toLowerCase().trim();
  const med = (params.utmMedium || "").toLowerCase().trim();
  const ref = (params.referrerHost || "").toLowerCase().trim();
  const hasFbclid = Boolean(params.fbclid);
  const hasGclid = Boolean(params.gclid);
  const hasTtclid = Boolean(params.ttclid);

  // 1. Meta (Facebook / Instagram). Facebook hangt een fbclid aan élke uitgaande klik, ook van
  // gewone berichten: alleen met een betaald utm_medium is het zeker een advertentie. Het kanaal
  // blijft meta_ads (zo staan ze bij elkaar in het dashboard), het label zegt wat we weten.
  const metaBron = woord(src, "facebook", "instagram", "meta", "fb", "ig");
  const betaald = /^(paid_social|paidsocial|cpc|ppc|paid|ads|ad)$/.test(med);
  if (hasFbclid || (metaBron && betaald)) {
    const kanaal = woord(src, "instagram", "ig") || /instagram/.test(ref)
      ? "Instagram"
      : woord(src, "facebook", "fb") || /facebook/.test(ref)
        ? "Facebook"
        : "";
    const naam = betaald ? "Meta Ads" : "Meta";
    return { channel: "meta_ads", channelLabel: kanaal ? `${naam} (${kanaal})` : naam };
  }

  // 2. Google Ads
  if (hasGclid || (src === "google" && /^(cpc|ppc|paid|adwords|ads)$/.test(med))) {
    return { channel: "google_ads", channelLabel: "Google Ads" };
  }

  // 3. TikTok Ads
  if (hasTtclid || (src.includes("tiktok") && /^(cpc|paid|ads)$/.test(med))) {
    return { channel: "tiktok_ads", channelLabel: "TikTok Ads" };
  }

  // 4. E-mail / Nieuwsbrief
  if (med === "email" || /klaviyo|newsletter|nieuwsbrief|mail/.test(src)) {
    return { channel: "email", channelLabel: "Nieuwsbrief (Klaviyo)" };
  }

  // 5. Organisch Google (alleen de zoekmachine zelf, niet mail.google.com e.d.)
  if (/^google\.[a-z.]+$/.test(ref) || (src === "google" && (!med || med === "organic"))) {
    return { channel: "google_organic", channelLabel: "Google (Organisch)" };
  }

  // 6. Overige zoekmachines
  if (/bing\.|duckduckgo\.|ecosia\.|yahoo\./.test(ref) || med === "organic") {
    const naam = ref.includes("bing") ? "Bing" : ref.includes("duckduckgo") ? "DuckDuckGo" : "Zoekmachine";
    return { channel: "organic_search", channelLabel: `${naam} (Organisch)` };
  }

  // 7. Social media (organisch)
  if (/instagram\.|facebook\.|fb\.me|tiktok\.|pinterest\.|linkedin\.|t\.co|twitter\.|threads\.net/.test(ref)) {
    let naam = "Social Media";
    if (ref.includes("instagram")) naam = "Instagram";
    else if (ref.includes("facebook") || ref.includes("fb.me")) naam = "Facebook";
    else if (ref.includes("tiktok")) naam = "TikTok";
    else if (ref.includes("pinterest")) naam = "Pinterest";
    return { channel: "social_organic", channelLabel: `${naam} (Organisch)` };
  }

  // 8. Verwijzende website
  if (ref && !ref.includes("mijnondergoedwinkel.nl") && !ref.includes("localhost")) {
    return { channel: "referral", channelLabel: `Verwijzing (${ref})` };
  }

  // 9. Campagne zonder specifiek kanaal
  if (src || params.utmCampaign) {
    return { channel: "referral", channelLabel: `Campagne (${src || params.utmCampaign})` };
  }

  // 10. Direct bezoek
  return { channel: "direct", channelLabel: "Direct bezoek" };
}

export interface AttributionInput {
  existing: OrderAttribution | null;
  now: string;
  pathname: string;
  search: string;
  /** Hostnaam van de eigen site, zonder www (zie extractHost). */
  ownHost: string | null;
  /**
   * document.referrer, maar ALLEEN als dit nog niet eerder is meegeteld (zie verwerkbareVerwijzer).
   * De referrer van een document verandert niet bij doorklikken binnen de site; telde hij elke keer,
   * dan overschreef de eerste klik na een advertentie de herkomst met "Instagram (Organisch)".
   */
  referrer: string;
  userAgent: string;
  /** Klik-ID's (fbclid/gclid/ttclid) bewaren? Alleen bij marketing-toestemming. */
  allowClickId: boolean;
}

/** Zoekopdracht voor de bewaarde landingspagina, zonder klik-ID's en bestelcode/token. */
export function schoneZoekopdracht(search: string): string {
  const p = new URLSearchParams(search);
  for (const k of GEWEERDE_PARAMS) p.delete(k);
  const s = p.toString();
  return s ? `?${s}` : "";
}

/**
 * Bepaalt de herkomst na een paginaweergave (puur, zonder browser). Last non-direct click: een
 * nieuwe campagne of externe verwijzer vervangt de oude herkomst; een gewone doorklik of een direct
 * bezoek binnen het venster laat de campagne staan en ververst alleen het tijdstip.
 */
export function nextAttribution(input: AttributionInput): OrderAttribution {
  const { existing, now } = input;
  const search = new URLSearchParams(input.search);
  const refHost = extractHost(input.referrer);

  const utmSource = search.get("utm_source");
  const utmMedium = search.get("utm_medium");
  const utmCampaign = search.get("utm_campaign");
  const utmContent = search.get("utm_content");
  const utmTerm = search.get("utm_term");
  const fbclid = search.get("fbclid");
  const gclid = search.get("gclid");
  const ttclid = search.get("ttclid");

  const hasMarketingParams = Boolean(utmSource || utmMedium || utmCampaign || fbclid || gclid || ttclid);
  const isExternalReferrer = Boolean(refHost && refHost !== input.ownHost && !isNegeerVerwijzer(refHost));

  // Niets nieuws: dezelfde herkomst, nieuw tijdstip.
  if (existing && !hasMarketingParams && !isExternalReferrer) {
    return { ...existing, lastTouchAt: now };
  }

  const { channel, channelLabel } = classifyChannel({
    utmSource,
    utmMedium,
    utmCampaign,
    fbclid,
    gclid,
    ttclid,
    referrerHost: isExternalReferrer ? refHost : null,
  });

  // Een direct bezoek haalt een eerdere campagne binnen het venster niet weg.
  if (channel === "direct" && existing && existing.channel !== "direct") {
    return { ...existing, lastTouchAt: now };
  }

  const clickId = input.allowClickId ? fbclid || gclid || ttclid || null : null;
  const clickIdType = clickId ? (fbclid ? "fbclid" : gclid ? "gclid" : "ttclid") : null;

  return {
    channel,
    channelLabel,
    utmSource: utmSource || null,
    utmMedium: utmMedium || null,
    utmCampaign: utmCampaign || null,
    utmContent: utmContent || null,
    utmTerm: utmTerm || null,
    clickId,
    clickIdType,
    referrer: isExternalReferrer ? input.referrer.split(/[?#]/)[0].slice(0, 300) : null,
    referrerHost: isExternalReferrer ? refHost : null,
    landingPage: `${input.pathname}${schoneZoekopdracht(input.search)}`.slice(0, 300),
    device: detectDevice(input.userAgent),
    firstTouchAt: existing?.firstTouchAt || now,
    lastTouchAt: now,
  };
}

let laatsteVerwijzer = ""; // terugval als sessionStorage niet bruikbaar is

/**
 * Geeft de referrer alleen terug als hij nog niet is meegeteld in deze tabsessie. Zo telt hij niet
 * mee bij doorklikken binnen de site (de referrer van een document verandert dan niet) en niet bij
 * herladen.
 */
export function verwerkbareVerwijzer(referrer: string): string {
  if (!referrer) return "";
  let eerder = laatsteVerwijzer;
  try {
    eerder = sessionStorage.getItem(REFERRER_KEY) || eerder;
    sessionStorage.setItem(REFERRER_KEY, referrer);
  } catch {
    /* sessionStorage kan geblokkeerd zijn */
  }
  laatsteVerwijzer = referrer;
  return eerder === referrer ? "" : referrer;
}

/** Client-side: leest de huidige URL en referrer en werkt de herkomst bij. Alleen aanroepen na toestemming. */
export function recordAttribution(opts: { allowClickId?: boolean } = {}): OrderAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const loc = window.location;
    const next = nextAttribution({
      existing: getStoredAttribution(),
      now: new Date().toISOString(),
      pathname: loc.pathname,
      search: loc.search,
      ownHost: extractHost(loc.origin),
      referrer: verwerkbareVerwijzer(document.referrer ? String(document.referrer) : ""),
      userAgent: navigator.userAgent,
      allowClickId: Boolean(opts.allowClickId),
    });
    saveAttribution(next);
    return next;
  } catch {
    return null;
  }
}

/** Haalt de opgeslagen herkomst op (alleen binnen het venster van 30 dagen). */
export function getStoredAttribution(): OrderAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || !parsed.channel) return null;
    const touch = parsed.lastTouchAt || parsed.firstTouchAt;
    if (touch) {
      const diffDays = (Date.now() - new Date(touch).getTime()) / (1000 * 60 * 60 * 24);
      if (!(diffDays <= ATTRIBUTION_WINDOW_DAYS)) return null;
    }
    return parsed as OrderAttribution;
  } catch {
    return null;
  }
}

/** Wist de bewaarde herkomst (bij het intrekken van toestemming). */
export function clearStoredAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(REFERRER_KEY);
  } catch {
    /* niets te wissen */
  }
  laatsteVerwijzer = "";
}

function saveAttribution(attr: OrderAttribution): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(attr));
  } catch {
    /* localStorage kan geblokkeerd zijn */
  }
}

/**
 * Server-side: saneert en valideert de herkomst die de client meegestuurd heeft (kassa) of die een
 * beheerder handmatig toewijst. Zonder (geldige) herkomst komt er null terug: de bestelling heeft
 * dan geen bekende herkomst. We verzinnen geen "direct" uit de Referer-header van het kassaverzoek;
 * die is altijd onze eigen site.
 */
export function sanitizeAttribution(
  raw: unknown,
  headers?: { get: (name: string) => string | null }
): OrderAttribution | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.channel !== "string" || !ATTRIBUTION_CHANNELS.includes(r.channel as AttributionChannel)) return null;

  const clean = (v: unknown, max: number = 200): string | null => {
    if (typeof v !== "string") return null;
    const t = v.trim().slice(0, max);
    return t || null;
  };
  const datum = (v: unknown): string => {
    const t = clean(v, 40);
    return t && !Number.isNaN(Date.parse(t)) ? t : new Date().toISOString();
  };

  const ua = headers?.get("user-agent") || "";
  const fallbackDevice = ua ? detectDevice(ua) : "desktop";

  return {
    channel: r.channel as AttributionChannel,
    channelLabel: clean(r.channelLabel, 80) || "Onbekend",
    utmSource: clean(r.utmSource, 100),
    utmMedium: clean(r.utmMedium, 100),
    utmCampaign: clean(r.utmCampaign, 150),
    utmContent: clean(r.utmContent, 150),
    utmTerm: clean(r.utmTerm, 150),
    clickId: clean(r.clickId, 150),
    clickIdType: r.clickIdType === "fbclid" || r.clickIdType === "gclid" || r.clickIdType === "ttclid" ? r.clickIdType : null,
    referrer: clean(r.referrer, 300),
    referrerHost: clean(r.referrerHost, 100),
    landingPage: clean(r.landingPage, 300),
    device: r.device === "mobile" || r.device === "desktop" || r.device === "tablet" ? r.device : fallbackDevice,
    firstTouchAt: datum(r.firstTouchAt),
    lastTouchAt: datum(r.lastTouchAt),
  };
}
